import { Grid, Typography } from "@mui/material";
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import ExportEmployeesApproversToExcel from "./ExportEmployeesApproversToExcel/ExportEmployeesApproversToExcel";
import TnAEmployeesApproversTable from "./TnAEmployeesApproversTable";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../../custom-hooks/useSearchOptions/useSearchOptions";
import { Page } from "../../../models/api/page";
import { EmployeeApproversDTO } from "../../../models/employee-approver/employee-approver-dto";
import { EmployeesFilter } from "../../../models/employees";
import { NotAuthorizedPage } from "../../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import CustomTablePagination from "../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { EmployeesApproversOverviewApiService } from "../../../services/employees-approvers-overview/employees-approvers-overview-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import TableHeader from "../../TableHeader/TableHeader";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";

const PAGE_SIZE = 10;

export interface State {
  loading: boolean;
  error: string;
  items: EmployeeApproversDTO[];
  totalItems: number;
  page: Page;
}
const TnAEmployeesApprovers = () => {
  const { t } = useTranslation();
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const tRP = "components.team_presence_and_leaves_approvers";

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves,
    AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData,
  ]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: PAGE_SIZE,
    },
  });

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions("");

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter, selectedLegalEntity },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
  } = useContext(MyTnAContext);

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));
      try {
        const response = await new EmployeesApproversOverviewApiService().loadItems(
          {
            legalEntityIds: legalEntityOption?.legalEntityIds,
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            // isActive : onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees
          } as EmployeesFilter,
          searchOptions.query,
          undefined,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );
        if (response.error) {
          response.error !== "canceled" && setState((state) => ({ ...state, error: response.error }));
        } else {
          setState((state) => ({ ...state, items: response.items, totalItems: response.totalItems }));
        }
      } catch (error) {
        error !== "canceled" && setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      legalEntityOption?.legalEntityIds,
      searchOptions.exactMatch,
      searchOptions.query,
      state.page,
    ]
  );

  const loadTnAEmployeeApproversData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new EmployeesApproversOverviewApiService().loadItems(
          {
            legalEntityIds: legalEntityOption?.legalEntityIds,
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
          } as EmployeesFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
    ]
  );

  const handleChangePageNumberAndSize = useCallback((field: "pageNumber" | "pageSize", value: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        [field]: value,
        ...(field === "pageSize" && {
          pageNumber: 0,
        }),
      },
    }));
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadData(abortController.signal);

    return () => abortController.abort();
  }, [
    loadData,
    employeeGroupOption?.employeeGroupIds,
    legalEntityOption?.legalEntityIds,
    onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
    searchOptions.exactMatch,
    searchOptions.query,
    state.page,
  ]);

  const loadEmployeeGroup = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  return (
    <>
      {hasAccess ? (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <TableHeader
            tableTitle={`${t(`${tRP}.table_title`)}`}
            quickFilters={
              <>
                {legalEntityQuickFilter}
                {employeeGroupQuickFilter}
                <ExportEmployeesApproversToExcel
                  loadEmployeeGroup={loadEmployeeGroup}
                  loadTnAEmployeeApproversData={loadTnAEmployeeApproversData}
                  disabled={state.loading || !state.items?.length}
                  state={state}
                />
                {/* {onlyCurrentEmployeesQuickFilter} */}
              </>
            }
            searchOptions={searchOptions}
            handleChangeSearchOptions={handleChangeSearchOptions}
          />
          <Grid
            style={{
              height: "62vh",
              overflow: "auto",
            }}
          >
            {state.items.length > 0 ? (
              <TnAEmployeesApproversTable items={state.items} />
            ) : (
              <Grid style={{ marginTop: "50px", textAlign: "center" }}>
                <Typography>No items</Typography>
              </Grid>
            )}
          </Grid>

          <CustomTablePagination
            totalItems={state.totalItems}
            pageNumber={state.page.pageNumber}
            pageSize={state.page.pageSize}
            handleChangePageNumber={(value) => handleChangePageNumberAndSize("pageNumber", value)}
            handleChangePageSize={(value) => handleChangePageNumberAndSize("pageSize", value)}
          />
        </LoadingOverlay>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};
export default TnAEmployeesApprovers;
